import { memo, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setIsOpened } from 'store/widget'
import { messagesActions, messagesSelectors } from 'store/messages'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { settingsActions, settingsSelectors } from 'store/settings'
import { OdinIcon } from '@/components/ui/odin/odin-icon'
import _ from 'lodash'
import { useModal } from 'providers/ModalProvider'
import { LoginModal } from 'components/modals/LoginModal'
import { ExportChatModal } from 'components/modals/ExportChatModal'
import { useGetBotMode } from 'hooks/useGetBotMode'
import { useIsAuthModeExternal, useIsAuthModeNoLogin } from 'hooks/useAuthMode'
import { useGetSetting } from 'api/settings'
import { AppVersionBanner } from 'components/app-version/AppVersionBanner'
import { useLogout } from 'hooks/useLogout'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'

const WidgetHeaderComponent = () => {
  const dispatch = useDispatch()
  const logout = useLogout()
  const { setting, title, description } = useGetSetting()
  const { t } = useTranslation()
  const chatId = useSelector(messagesSelectors.chatId)
  const isLogged = useSelector(settingsSelectors.isLogged)
  const showExpandButton = setting?.showWidgetExpandButton === true
  const { isAppMode, isWebMode } = useGetBotMode()
  const { openModal } = useModal()

  const isExternal = useIsAuthModeExternal()
  const isNoLogin = useIsAuthModeNoLogin()

  // Menu actions run only after the menu's exit transition ends (onOpenChangeComplete): the
  // Close item hides the whole widget, and a body-portaled popup whose anchor just went
  // display:none would flash its exit animation at the page origin.
  const pendingActionRef = useRef<(() => void) | null>(null)
  const onMenuClosed = (open: boolean) => {
    if (open) return
    const action = pendingActionRef.current
    pendingActionRef.current = null
    action?.()
  }

  const dropdownMenu = useMemo(() => {
    return _.compact([
      isLogged &&
        !isExternal && {
          title: t('logout'),
          callback: logout,
          icon: <OdinIcon icon={['far', 'sign-out-alt']} className="h-4 w-4" />,
        },
      !isLogged &&
        !isExternal &&
        !isNoLogin && {
          title: t('login'),
          callback: () => openModal(<LoginModal />),
          icon: <OdinIcon icon={['far', 'sign-in-alt']} className="h-4 w-4" />,
        },
      isWebMode && {
        title: t('close'),
        icon: <OdinIcon icon={['far', 'times']} className="h-4 w-4" />,
        callback: () => dispatch(setIsOpened(false)),
      },
      showExpandButton && {
        title: t('expand'),
        icon: <OdinIcon icon={['far', 'expand']} className="h-4 w-4" />,
        callback: () => window.parent.postMessage({ type: 'expandWidget' }, '*'),
      },
      !!chatId && {
        title: t('goBack'),
        icon: <OdinIcon icon={['far', 'arrow-left']} className="h-4 w-4" />,
        callback: () => {
          dispatch(messagesActions.setChatId(undefined))
          dispatch(settingsActions.setTicketReminderVisible(false))
        },
      },
      isLogged &&
        !!chatId && {
          title: t('exportChat'),
          callback: () => openModal(<ExportChatModal />),
          icon: <OdinIcon icon={['far', 'file-export']} className="h-4 w-4" />,
        },
    ])
  }, [t, chatId, isLogged, isWebMode, isExternal, isNoLogin, showExpandButton, openModal, dispatch, logout])

  return (
    <div
      className={clsx(
        'relative flex h-18 shrink-0 cursor-default items-center gap-2 overflow-hidden bg-primary p-4 text-primary-foreground',
        isAppMode ? '' : 'rounded-t-2xl',
      )}
    >
      {setting?.logo?.full && (
        <div className="shrink-0 p-1">
          <img
            className="h-10 w-10 rounded-md"
            src={setting?.logo?.thumbnail?.medium ?? setting?.logo}
            alt="Bot Logo"
          />
        </div>
      )}
      <div className="flex-1">
        <div className="text-base font-bold">{title}</div>
        <p className="text-sm text-primary-foreground/80">{description}</p>
        <AppVersionBanner />
      </div>
      {dropdownMenu.length > 0 && (
        <DropdownMenu onOpenChangeComplete={onMenuClosed}>
          <DropdownMenuTrigger
            aria-label={t('menu')}
            className="flex cursor-pointer border-0 bg-transparent p-2 text-primary-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-solid"
          >
            <OdinIcon icon={['far', 'bars']} size={'1x'} />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-auto">
            {dropdownMenu.map((item, idx) => (
              <DropdownMenuItem
                key={idx}
                onClick={() => {
                  pendingActionRef.current = item.callback
                }}
              >
                {item.icon}
                <span>{item.title}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}

export const WidgetHeader = memo(WidgetHeaderComponent)
